import React, { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { DashboardLayout } from '../components/Layout'
import { Button, Input, Textarea, Avatar, toast, Spinner } from '../components/UI'
import { Bell, User, Building2, Shield, Check } from 'lucide-react'

// ── Settings Page ─────────────────────────────────────────────
export function SettingsPage() {
  const { profile, updateProfile, user } = useAuth()
  const [tab, setTab] = useState('profile')
  const [form, setForm] = useState({ full_name: '', company_name: '', phone: '', bio: '', website: '' })
  const [loading, setLoading] = useState(false)
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' })
  const [pwLoading, setPwLoading] = useState(false)

  useEffect(() => {
    if (profile) {
      setForm({
        full_name: profile.full_name || '',
        company_name: profile.company_name || '',
        phone: profile.phone || '',
        bio: profile.bio || '',
        website: profile.website || '',
      })
    }
  }, [profile])

  async function handleSaveProfile() {
    setLoading(true)
    try {
      await updateProfile(form)
      toast.success('Profile updated!')
    } catch (e) {
      toast.error(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleChangePassword() {
    if (pwForm.newPw !== pwForm.confirm) { toast.error('Passwords do not match'); return }
    if (pwForm.newPw.length < 8) { toast.error('Minimum 8 characters'); return }
    setPwLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password: pwForm.newPw })
      if (error) throw error
      toast.success('Password updated!')
      setPwForm({ current: '', newPw: '', confirm: '' })
    } catch (e) {
      toast.error(e.message)
    } finally {
      setPwLoading(false)
    }
  }

  const tabs = [
    { id: 'profile', icon: <User size={16} />, label: 'Profile' },
    { id: 'company', icon: <Building2 size={16} />, label: 'Company' },
    { id: 'security', icon: <Shield size={16} />, label: 'Security' },
  ]

  return (
    <DashboardLayout title="Settings">
      <div style={{ maxWidth: 720, display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24 }}>
        {/* Sidebar tabs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              borderRadius: 'var(--radius)', border: 'none', cursor: 'pointer', textAlign: 'left',
              background: tab === t.id ? 'var(--accent-glow)' : 'transparent',
              color: tab === t.id ? 'var(--tech)' : 'var(--gray-600)',
              fontWeight: tab === t.id ? 600 : 400, fontSize: '0.9rem',
              transition: 'all 0.15s'
            }}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="card" style={{ padding: 28 }}>
          {tab === 'profile' && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
                <Avatar name={profile?.full_name || profile?.email} size={60} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1.1rem' }}>{profile?.full_name || 'Your Name'}</div>
                  <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)' }}>{profile?.email}</div>
                  <div style={{ marginTop: 4 }}>
                    <span style={{
                      fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                      background: profile?.role === 'media_owner' ? '#DBEAFE' : '#D1FAE5',
                      color: profile?.role === 'media_owner' ? '#1E40AF' : '#065F46'
                    }}>
                      {profile?.role === 'media_owner' ? 'Media Owner' : 'Advertiser'}
                    </span>
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div className="grid-2">
                  <Input label="Full Name" value={form.full_name} onChange={e => setForm(p => ({ ...p, full_name: e.target.value }))} />
                  <Input label="Phone Number" type="tel" placeholder="+971 50 000 0000" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} />
                </div>
                <Textarea label="Bio" placeholder="Tell others about yourself..." rows={3} value={form.bio} onChange={e => setForm(p => ({ ...p, bio: e.target.value }))} />
                <Button onClick={handleSaveProfile} loading={loading} icon={<Check size={16} />} style={{ alignSelf: 'flex-end' }}>
                  Save Changes
                </Button>
              </div>
            </>
          )}

          {tab === 'company' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 4 }}>Company Information</h3>
              <Input label="Company Name" value={form.company_name} onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))} />
              <Input label="Website" type="url" placeholder="https://yourcompany.com" value={form.website} onChange={e => setForm(p => ({ ...p, website: e.target.value }))} />
              <Button onClick={handleSaveProfile} loading={loading} icon={<Check size={16} />} style={{ alignSelf: 'flex-end' }}>
                Save Company Info
              </Button>
            </div>
          )}

          {tab === 'security' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, marginBottom: 4 }}>Change Password</h3>
              <Input label="New Password" type="password" placeholder="Min 8 characters" value={pwForm.newPw} onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))} />
              <Input label="Confirm New Password" type="password" placeholder="Repeat password" value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} />
              <Button onClick={handleChangePassword} loading={pwLoading} icon={<Shield size={16} />} style={{ alignSelf: 'flex-end' }}>
                Update Password
              </Button>

              <div style={{ marginTop: 8, padding: 16, background: 'var(--gray-50)', borderRadius: 8 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 4 }}>Logged in as</div>
                <div style={{ fontSize: '0.88rem', color: 'var(--gray-500)' }}>{user?.email}</div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  )
}

// ── Notifications Page ─────────────────────────────────────────
export function NotificationsPage() {
  const { profile } = useAuth()
  const [notifications, setNotifications] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(50)
      setNotifications(data || [])
      setLoading(false)
    }
    if (profile) load()
  }, [profile])

  async function markAllRead() {
    await supabase.from('notifications').update({ is_read: true }).eq('user_id', profile.id)
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
  }

  const typeIcon = { booking: '📋', payment: '💰', artwork: '🎨', system: '⚙️', inquiry: '💬' }

  return (
    <DashboardLayout title="Notifications" actions={
      <Button variant="ghost" size="sm" onClick={markAllRead}>Mark all read</Button>
    }>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner size={36} /></div>
      ) : notifications.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--gray-400)' }}>
          <Bell size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>No notifications yet</p>
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {notifications.map((n, i) => (
            <div key={n.id} style={{
              display: 'flex', gap: 14, padding: '14px 20px',
              background: n.is_read ? 'white' : 'rgba(48,102,190,0.04)',
              borderBottom: i < notifications.length - 1 ? '1px solid var(--gray-50)' : 'none',
              transition: 'background 0.15s'
            }}>
              <div style={{ fontSize: '1.2rem', width: 32, textAlign: 'center', flexShrink: 0, marginTop: 2 }}>
                {typeIcon[n.type] || '🔔'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: n.is_read ? 500 : 700, marginBottom: 2 }}>{n.title}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--gray-500)', lineHeight: 1.5 }}>{n.message}</div>
                <div style={{ fontSize: '0.75rem', color: 'var(--gray-300)', marginTop: 4 }}>
                  {new Date(n.created_at).toLocaleDateString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
              {!n.is_read && (
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--tech)', flexShrink: 0, marginTop: 8 }} />
              )}
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  )
}
